import React, { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { tl, stat } from "@/lib/i18nFormat";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, LoadingBlock, EmptyState, StatusPill } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CreditCard, Bell, KeyRound, FileText, Activity, AlertTriangle, ArrowRight, DatabaseBackup, ShieldCheck, RotateCcw, Play } from "lucide-react";

import { useI18n } from "@/context/I18nContext";

const healthPill = (state: string) => (state === "ok" ? "good" : state === "attention" ? "warn" : "inactive");

const Platform: React.FC = () => {
  const {
    t
  } = useI18n();

  const { hasPerm } = useAuth();
  const canView = hasPerm("platform.view");
  const isAdmin = hasPerm("platform.admin");
  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState<any>(null);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [email, setEmail] = useState<any>(null);
  const [identity, setIdentity] = useState<any>(null);
  const [health, setHealth] = useState<any>(null);

  const load = useCallback(async () => {
    try {
      const [acc, inv, em, idn, hlt] = await Promise.all([
        api.get("/platform/billing/account"),
        api.get("/platform/billing/invoices"),
        api.get("/platform/email/config"),
        api.get("/platform/identity/config"),
        api.get("/platform/health/overview"),
      ]);
      setAccount(acc.data); setInvoices(inv.data.invoices || []);
      setEmail(em.data); setIdentity(idn.data); setHealth(hlt.data);
    } catch { toast.error("Could not load platform services"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (canView) load(); else setLoading(false); }, [load, canView]);
  if (!canView) return <Navigate to="/today" replace />;
  if (loading) return <LoadingBlock />;

  return (
    (<div className="space-y-6" data-testid="platform-page">
      <PageHeader title="Platform Services" subtitle="Billing, notifications and enterprise identity" />
      <Tabs defaultValue="health">
        <TabsList data-testid="platform-tabs" className="max-w-full flex-nowrap overflow-x-auto">
          <TabsTrigger value="health" data-testid="tab-health" className="shrink-0"><Activity size={15} className="mr-1.5" />{t("Operational Health")}</TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing" className="shrink-0"><CreditCard size={15} className="mr-1.5" />{t("Billing")}</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications" className="shrink-0"><Bell size={15} className="mr-1.5" />{t("Notifications")}</TabsTrigger>
          <TabsTrigger value="identity" data-testid="tab-identity" className="shrink-0"><KeyRound size={15} className="mr-1.5" />{t("Identity")}</TabsTrigger>
          <TabsTrigger value="backup" data-testid="tab-backup" className="shrink-0"><DatabaseBackup size={15} className="mr-1.5" />{t("Backup & DR")}</TabsTrigger>
        </TabsList>
        <TabsContent value="health" className="mt-4"><HealthTab health={health} /></TabsContent>
        <TabsContent value="billing" className="mt-4"><BillingTab account={account} invoices={invoices} isAdmin={isAdmin} onChange={load} /></TabsContent>
        <TabsContent value="notifications" className="mt-4"><NotificationsTab email={email} isAdmin={isAdmin} onChange={load} /></TabsContent>
        <TabsContent value="identity" className="mt-4"><IdentityTab identity={identity} isAdmin={isAdmin} onChange={load} /></TabsContent>
        <TabsContent value="backup" className="mt-4"><BackupTab isAdmin={isAdmin} /></TabsContent>
      </Tabs>
    </div>)
  );
};

const BillingTab: React.FC<{ account: any; invoices: any[]; isAdmin: boolean; onChange: () => void }> = ({ account, invoices, isAdmin, onChange }) => {
  const {
    t
  } = useI18n();

  const [price, setPrice] = useState("");
  const [period, setPeriod] = useState("");
  const [locations, setLocations] = useState("");
  const [onboarding, setOnboarding] = useState("");
  const [deling, setDeling] = useState<any>(null);
  const [summary, setSummary] = useState<any>(null);
  const loadSummary = () => api.get("/platform/billing/summary").then((r) => setSummary(r.data)).catch(() => {});
  useEffect(() => {
    if (isAdmin) api.get("/platform/billing/delinquency").then((r) => setDeling(r.data)).catch(() => {});
    loadSummary();
  }, [isAdmin]);
  if (!account) return <LoadingBlock />;
  const u = account.usage || {};
  const money = (cents: number) => `$${(((cents || 0) / 100)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const createInvoice = async () => {
    if (!period.trim() || !price) return toast.error("Period and unit price required");
    try { await api.post("/platform/billing/invoices", { period_label: period, unit_price: parseFloat(price) }); toast.success("Invoice created"); setPeriod(""); onChange(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const setStatus = async (id: string, status: string) => {
    try { await api.post(`/platform/billing/invoices/${id}/status`, { status }); toast.success(tl("Marked {status}", { status })); onChange(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveContract = async () => {
    const body: any = {};
    if (locations.trim()) body.contracted_locations = parseInt(locations, 10);
    if (onboarding) body.onboarding_status = onboarding;
    if (!Object.keys(body).length) return;
    try { await api.put("/platform/billing/account", body); toast.success(tl("Contract updated")); setLocations(""); setOnboarding(""); onChange(); loadSummary(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const startCheckout = async (planKey: string) => {
    try {
      const r = await api.post("/platform/billing/checkout", { plan_key: planKey, origin_url: window.location.origin });
      if (r.data.status === "not_configured") toast.message(tl("Stripe Not Configured"), { description: r.data.detail });
      else if (r.data.checkout_url) window.location.href = r.data.checkout_url;
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openPortal = async () => {
    try {
      const r = await api.post("/platform/billing/portal", { return_url: window.location.href });
      if (r.data.status === "not_configured") toast.message(tl("Stripe Not Configured"), { description: r.data.detail });
      else if (r.data.portal_url) window.location.href = r.data.portal_url;
    } catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const decideDeling = async (decision: string) => {
    try { const r = await api.post("/platform/billing/delinquency/decision", { decision }); toast.success(r.data.detail); const d = await api.get("/platform/billing/delinquency"); setDeling(d.data); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    (<div className="space-y-4" data-testid="billing-tab">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Card className="p-4"><p className="text-xs text-muted-foreground">{t("Active stores")}</p><p className="text-2xl font-semibold" data-testid="billing-active-stores">{u.active_store_count ?? 0}</p></Card>
        <Card className="p-4"><p className="text-xs text-muted-foreground">{t("Total stores")}</p><p className="text-2xl font-semibold">{u.store_count ?? 0}</p></Card>
        <Card className="p-4"><p className="text-xs text-muted-foreground">{t("Active users")}</p><p className="text-2xl font-semibold">{u.user_count ?? 0}</p></Card>
        <Card className="p-4"><p className="text-xs text-muted-foreground">{t("Stripe")}</p><StatusPill status={account.stripe_status === "test_mode" ? "info" : "inactive"}>{stat(account.stripe_status)}</StatusPill></Card>
      </div>
      <Card className="p-4">
        <p className="text-sm font-medium">{t("Billing basis")}</p>
        <p className="text-xs text-muted-foreground">{t(
          "Per active store · manual invoice administration is authoritative. No subscription tiers or paywalls."
        )}</p>
        <p className="mt-2 text-sm">{t("Unit price:")}{" "}<span className="font-medium">{account.unit_price == null ? t("Not set") : `${account.unit_price} ${account.currency || "usd"}`}</span>{" "}{t("· Implementation:")}{" "}{stat(account.implementation_status)}</p>
        {isAdmin && (
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <div><Label className="text-xs">{t("Contracted locations")}</Label><Input value={locations} onChange={(e) => setLocations(e.target.value)} className="h-9 w-40" placeholder={String(u.active_store_count ?? 1)} data-testid="billing-contracted-locations" /></div>
            <div>
              <Label className="text-xs">{t("Onboarding")}</Label>
              <select value={onboarding} onChange={(e) => setOnboarding(e.target.value)} className="h-9 w-40 rounded-md border border-border bg-background px-2 text-sm" data-testid="billing-onboarding-status">
                <option value="">{t("(unchanged)")}</option>
                <option value="due">{t("Due")}</option>
                <option value="paid">{t("Paid")}</option>
                <option value="waived">{t("Waived")}</option>
                <option value="credited">{t("Credited")}</option>
                <option value="invoiced">{t("Invoiced")}</option>
                <option value="not_applicable">{t("Not applicable")}</option>
              </select>
            </div>
            <Button size="sm" variant="outline" onClick={saveContract} data-testid="billing-save-contract">{t("Save contract")}</Button>
          </div>
        )}
      </Card>
      {(
        <Card className="p-4" data-testid="stripe-collection">
          <p className="text-sm font-medium">{t("Subscription & activation")}{" "}<StatusPill status={account.stripe_status === "test_mode" ? "info" : "inactive"}>{stat(account.stripe_status)}</StatusPill></p>
          <p className="text-xs text-muted-foreground">{t("Initial launch pricing. All standard modules and unlimited users included. Billing is per contracted active location.")}</p>
          {summary && (
            <>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-border p-3" data-testid="plan-monthly">
                  <p className="text-sm font-semibold">{t("Monthly")}</p>
                  <p className="text-2xl font-bold">{money(summary.plans?.monthly?.unit_amount)}<span className="text-xs font-normal text-muted-foreground">{t("/location/month")}</span></p>
                  <p className="text-xs text-muted-foreground">{summary.billed_quantity}{" "}{t("contracted locations")}{" · "}<span className="font-medium">{money((summary.plans?.monthly?.unit_amount || 0) * (summary.billed_quantity || 0))}{t("/month")}</span></p>
                  {isAdmin && <Button size="sm" className="mt-2 w-full" onClick={() => startCheckout("monthly")} data-testid="activate-monthly-btn">{t("Activate monthly")}</Button>}
                </div>
                <div className="rounded-lg border border-border p-3" data-testid="plan-annual">
                  <p className="text-sm font-semibold">{t("Annual")}</p>
                  <p className="text-2xl font-bold">{money(summary.plans?.annual?.unit_amount)}<span className="text-xs font-normal text-muted-foreground">{t("/location/year")}</span></p>
                  <p className="text-xs text-muted-foreground">{t("Equivalent to $169/location/month")}</p>
                  <p className="text-xs text-muted-foreground">{summary.billed_quantity}{" "}{t("contracted locations")}{" · "}<span className="font-medium">{money((summary.plans?.annual?.unit_amount || 0) * (summary.billed_quantity || 0))}{t("/year")}</span></p>
                  {isAdmin && <Button size="sm" className="mt-2 w-full" onClick={() => startCheckout("annual")} data-testid="activate-annual-btn">{t("Activate annual")}</Button>}
                </div>
              </div>
              {summary.onboarding_will_be_charged && (
                <p className="mt-3 text-xs text-foreground" data-testid="onboarding-notice">{t("Standard onboarding")}{" ("}{money(summary.plans?.onboarding?.unit_amount)}{t(", up to 10 locations) will be added to this activation.")}</p>
              )}
              <p className="mt-1 text-xs text-muted-foreground" data-testid="onboarding-status">{t("Onboarding status:")}{" "}{stat(summary.onboarding_status)}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2 text-xs" data-testid="billing-status">
                <span>{t("Billing status:")}</span>
                <StatusPill status={summary.billing_status === "active" ? "good" : summary.billing_status === "canceled" ? "inactive" : summary.billing_status === "none" ? "info" : "warn"}>{stat(summary.billing_status)}</StatusPill>
                {summary.subscription?.current_period_end && <span className="text-muted-foreground">{t("Renews:")}{" "}{new Date(summary.subscription.current_period_end * 1000).toLocaleDateString()}</span>}
              </div>
              {(summary.billing_status === "past_due" || summary.billing_status === "action_required") && (
                <div className="mt-2 rounded-md border border-amber-400 bg-amber-50 p-2 text-xs text-amber-800" data-testid="payment-issue-notice">
                  {t("A recent payment needs attention. Your access is unchanged — resolve it in the billing portal or contact your administrator.")}
                </div>
              )}
              {isAdmin && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={openPortal} data-testid="stripe-portal-btn">{t("Manage billing (portal)")}</Button>
                </div>
              )}
              {!summary.configured && <p className="mt-2 text-xs text-muted-foreground" data-testid="billing-not-configured">{t("Billing is not configured yet — contact Titan Haven Group to activate.")}</p>}
            </>
          )}
          {isAdmin && deling && deling.state !== "none" && !deling.resolved && (
            <div className="mt-3 rounded-md border border-amber-400 bg-amber-50 p-3" data-testid="delinquency-banner">
              <p className="text-sm font-medium text-amber-800">{t("Delinquency flagged — awaiting admin decision")}</p>
              <p className="text-xs text-amber-700">{t("Reason:")}{" "}{deling.reason || "payment issue"}{t(". Entitlement is unchanged until you decide.")}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" onClick={() => decideDeling("acknowledge")} data-testid="deling-ack">{t("Acknowledge")}</Button>
                <Button size="sm" variant="outline" onClick={() => decideDeling("grace_extended")} data-testid="deling-grace">{t("Extend grace")}</Button>
                <Button size="sm" variant="outline" onClick={() => decideDeling("cleared")} data-testid="deling-clear">{t("Mark cleared")}</Button>
              </div>
            </div>
          )}
        </Card>
      )}
      {isAdmin && (
        <Card className="p-4" data-testid="invoice-create">
          <p className="mb-2 text-sm font-medium">{t("Raise invoice")}</p>
          <div className="flex flex-wrap items-end gap-2">
            <div><Label className="text-xs">{t("Period")}</Label><Input value={period} onChange={(e) => setPeriod(e.target.value)} className="h-9 w-36" placeholder="2026-06" data-testid="invoice-period" /></div>
            <div><Label className="text-xs">{t("Unit price")}</Label><Input value={price} onChange={(e) => setPrice(e.target.value)} className="h-9 w-32" placeholder="12.50" data-testid="invoice-price" /></div>
            <Button size="sm" onClick={createInvoice} data-testid="invoice-create-btn">{t("Create invoice")}</Button>
          </div>
        </Card>
      )}
      {!invoices.length ? <EmptyState title="No invoices" description="Manual invoices you raise appear here." /> : (
        <div className="space-y-2">
          {invoices.map((iv) => (
            <Card key={iv.id} className="flex flex-wrap items-center justify-between gap-3 p-3" data-testid={`invoice-${iv.id}`}>
              <div>
                <div className="flex items-center gap-2"><p className="font-medium">{iv.period_label}</p><StatusPill status={iv.status === "paid" ? "good" : iv.status === "void" ? "inactive" : "info"}>{stat(iv.status)}</StatusPill></div>
                <p className="text-xs text-muted-foreground">{iv.line_items?.[0]?.quantity}{" "}{t("active stores ×")}{" "}{iv.line_items?.[0]?.unit_price}= {iv.amount_total} {iv.currency}</p>
              </div>
              {isAdmin && iv.status !== "paid" && iv.status !== "void" && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setStatus(iv.id, "sent")} data-testid={`invoice-send-${iv.id}`}>{t("Mark sent")}</Button>
                  <Button size="sm" variant="outline" onClick={() => setStatus(iv.id, "paid")} data-testid={`invoice-paid-${iv.id}`}>{t("Mark paid")}</Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>)
  );
};

const NotificationsTab: React.FC<{ email: any; isAdmin: boolean; onChange: () => void }> = ({ email, isAdmin, onChange }) => {
  const {
    t
  } = useI18n();

  const [domain, setDomain] = useState("");
  const [key, setKey] = useState("");
  const enablePush = async () => {
    try {
      const reg = "serviceWorker" in navigator ? await navigator.serviceWorker?.getRegistration?.() : null;
      const pk = (await api.get("/platform/push/public-key")).data.application_server_key;
      if (reg && "pushManager" in reg) {
        const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: pk });
        await api.post("/platform/push/subscribe", { subscription: sub.toJSON() });
        toast.success("Push enabled on this browser");
      } else { toast.message("Push public key retrieved; no service worker registered in this environment"); }
    } catch { toast.error("Push not available in this browser/environment"); }
  };
  const testPush = async () => {
    try { const r = await api.post("/platform/push/test"); toast.success(tl("Push sent to {n} device(s)", { n: r.data.sent })); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveEmail = async () => {
    try { await api.put("/platform/email/config", { api_key: key || undefined, from_domain: domain || undefined }); toast.success("Email settings saved"); setKey(""); onChange(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    (<div className="space-y-4" data-testid="notifications-tab">
      <Card className="p-4">
        <p className="text-sm font-medium">{t("Web push (VAPID)")}</p>
        <p className="text-xs text-muted-foreground">{t(
          "Server-generated keys — no third party. In-app notifications remain authoritative."
        )}</p>
        <div className="mt-3 flex gap-2">
          <Button size="sm" onClick={enablePush} data-testid="push-enable">{t("Enable on this browser")}</Button>
          {isAdmin && <Button size="sm" variant="outline" onClick={testPush} data-testid="push-test">{t("Send test")}</Button>}
        </div>
      </Card>
      <Card className="p-4">
        <div className="flex items-center gap-2"><p className="text-sm font-medium">{t("Email (Resend)")}</p><StatusPill status={email?.status === "configured" ? "good" : "inactive"}>{stat(email?.status || "not_configured")}</StatusPill></div>
        <p className="text-xs text-muted-foreground">{t(
          "Not Configured until a Resend API key and a verified from-domain are supplied. The key is stored securely and never displayed."
        )}</p>
        {isAdmin && (
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <div><Label className="text-xs">{t("From domain")}</Label><Input value={domain} onChange={(e) => setDomain(e.target.value)} className="h-9 w-52" placeholder={t("mail.company.com")} data-testid="email-domain" /></div>
            <div><Label className="text-xs">{t("Resend API key")}</Label><Input value={key} onChange={(e) => setKey(e.target.value)} type="password" className="h-9 w-52" placeholder={t("re_...")} data-testid="email-key" /></div>
            <Button size="sm" onClick={saveEmail} data-testid="email-save">{t("Save")}</Button>
          </div>
        )}
      </Card>
    </div>)
  );
};

const IdentityTab: React.FC<{ identity: any; isAdmin: boolean; onChange: () => void }> = ({ identity, isAdmin, onChange }) => {
  const {
    t
  } = useI18n();

  const [proto, setProto] = useState("oidc");
  const [issuer, setIssuer] = useState("");
  const save = async () => {
    try { await api.put("/platform/identity/config", { protocol: proto, issuer: issuer || undefined }); toast.success("Identity settings saved (stays Not Configured until a real IdP is verified)"); setIssuer(""); onChange(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!identity) return <LoadingBlock />;
  return (
    (<div className="space-y-4" data-testid="identity-tab">
      {["oidc", "saml", "scim"].map((p) => (
        <Card key={p} className="flex items-center justify-between p-4" data-testid={`identity-${p}`}>
          <div>
            <div className="flex items-center gap-2"><p className="font-medium uppercase">{p}</p><StatusPill status="inactive">{stat(identity[p]?.status)}</StatusPill></div>
            <p className="text-xs text-muted-foreground">{identity[p]?.note}</p>
          </div>
          <FileText size={18} className="text-muted-foreground" />
        </Card>
      ))}
      {isAdmin && (
        <Card className="p-4" data-testid="identity-config">
          <p className="mb-2 text-sm font-medium">{t("Configure a provider")}</p>
          <div className="flex flex-wrap items-end gap-2">
            <div><Label className="text-xs">{t("Protocol")}</Label><Input value={proto} onChange={(e) => setProto(e.target.value)} className="h-9 w-28" data-testid="identity-proto" /></div>
            <div><Label className="text-xs">{t("Issuer / Metadata URL")}</Label><Input value={issuer} onChange={(e) => setIssuer(e.target.value)} className="h-9 w-72" placeholder="https://idp.company.com" data-testid="identity-issuer" /></div>
            <Button size="sm" onClick={save} data-testid="identity-save">{t("Save")}</Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{t("Service-provider metadata: OIDC")}{" "}{identity.sp_metadata?.oidc_redirect_uri}{" "}{t("· SAML")}{" "}{identity.sp_metadata?.saml_acs_url}{" "}{t("· SCIM")}{" "}{identity.sp_metadata?.scim_base_url}</p>
        </Card>
      )}
    </div>)
  );
};

const HealthTab: React.FC<{ health: any }> = ({ health }) => {
  const {
    t
  } = useI18n();

  if (!health) return <LoadingBlock />;
  const anyAttention = [health.webhooks, health.imports, health.exports, health.exceptions, ...(health.queues || [])]
    .some((s: any) => s?.state === "attention");
  const fmtStatuses = (by: Record<string, number>) =>
    Object.keys(by || {}).length ? Object.entries(by).map(([k, v]) => `${stat(k)}: ${v}`).join(" · ") : tl("no jobs");

  return (
    (<div className="space-y-4" data-testid="health-tab">
      <Card className="flex flex-wrap items-center justify-between gap-3 p-4" data-testid="health-summary">
        <div className="flex items-center gap-2">
          <Activity size={18} className={anyAttention ? t("text-status-warn") : t("text-status-good")} />
          <div>
            <p className="text-sm font-medium">{t("Operational health")}</p>
            <p className="text-xs text-muted-foreground">{t(
              "Aggregated from existing signals — job queues, webhooks, imports/exports, exceptions, billing. Read-only."
            )}</p>
          </div>
        </div>
        <StatusPill status={anyAttention ? "warn" : "good"} testId="health-overall-pill">
          {anyAttention ? t("Attention needed") : t("All clear")}
        </StatusPill>
      </Card>
      {/* Job queues */}
      <div className="grid gap-3 sm:grid-cols-3">
        {(health.queues || []).map((q: any) => (
          <Card key={q.key} className="p-4" data-testid={`health-queue-${q.key}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium">{tl(q.name)}</p>
              <StatusPill status={healthPill(q.state)}>{q.state === "ok" ? t("ok") : t("attention")}</StatusPill>
            </div>
            <p className="mt-2 text-2xl font-semibold" data-testid={`health-queue-total-${q.key}`}>{q.total}</p>
            <p className="text-xs text-muted-foreground">{fmtStatuses(q.by_status)}</p>
            {q.failed > 0 && <p className="mt-1 text-xs font-medium text-status-warn">{q.failed}{" "}{t("failed / dead-letter")}</p>}
          </Card>
        ))}
      </div>
      {/* Webhooks + imports + exports */}
      <div className="grid gap-3 sm:grid-cols-3">
        <Card className="p-4" data-testid="health-webhooks">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium">{t("Webhook deliveries")}</p>
            <StatusPill status={healthPill(health.webhooks.state)}>{health.webhooks.state === "ok" ? t("ok") : t("attention")}</StatusPill>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{t("Delivered")}{" "}{health.webhooks.delivered}{" "}{t("· Failed")}{" "}{health.webhooks.failed}{" "}{t("· Dead-letter")}{" "}{health.webhooks.dead_letter}
          </p>
        </Card>
        <Card className="p-4" data-testid="health-imports">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium">{t("Imports")}</p>
            <StatusPill status={healthPill(health.imports.state)}>{health.imports.state === "ok" ? t("ok") : t("attention")}</StatusPill>
          </div>
          <p className="mt-2 text-2xl font-semibold">{health.imports.total}</p>
          <p className="text-xs text-muted-foreground">{fmtStatuses(health.imports.by_status)}</p>
        </Card>
        <Card className="p-4" data-testid="health-exports">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium">{t("Exports")}</p>
            <StatusPill status={healthPill(health.exports.state)}>{health.exports.state === "ok" ? t("ok") : t("attention")}</StatusPill>
          </div>
          <p className="mt-2 text-2xl font-semibold">{health.exports.total}</p>
          <p className="text-xs text-muted-foreground">{fmtStatuses(health.exports.by_status)}</p>
        </Card>
      </div>
      {/* Exceptions -> Exception Center */}
      <Card className="p-4" data-testid="health-exceptions">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className={health.exceptions.state === "attention" ? t("text-status-warn") : t("text-muted-foreground")} />
            <div>
              <p className="text-sm font-medium">{t("Open exceptions")}</p>
              <p className="text-xs text-muted-foreground">{t("Integration")}{" "}{health.exceptions.open_integration}{" "}{t("· Reconciliation")}{" "}{health.exceptions.open_reconciliation}
              </p>
            </div>
          </div>
          <Link to="/exceptions" data-testid="health-exception-center-link">
            <Button size="sm" variant="outline">{t("Open Exception Center")}{" "}<ArrowRight size={14} className="ml-1.5" /></Button>
          </Link>
        </div>
      </Card>
      {/* Billing + backup honesty */}
      <div className="grid gap-3 sm:grid-cols-2">
        <Card className="p-4" data-testid="health-billing">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium">{t("Billing (Stripe)")}</p>
            <StatusPill status={health.billing.state === "ok" ? "good" : "inactive"}>{stat(health.billing.stripe_status)}</StatusPill>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">{t("Webhooks processed:")}{" "}{health.billing.stripe_events_processed}{t(". Manual invoice administration stays authoritative.")}</p>
        </Card>
        <Card className="p-4" data-testid="health-backup">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium">{t("Backup & recovery")}</p>
            <StatusPill status={health.backup.state === "ok" ? "good" : health.backup.state === "attention" ? "warn" : "inactive"} testId="health-backup-pill">
              {health.backup.state === "not_configured" ? t("Not Configured") : health.backup.state === "attention" ? t("Stale") : t("Protected")}
            </StatusPill>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {health.backup.configured
              ? `${tl("Last backup")} ${health.backup.last_backup_age_hours != null ? tl("{h}h ago", { h: health.backup.last_backup_age_hours }) : tl("recorded")} · ${tl("Encryption")} ${health.backup.encryption_enabled ? tl("on") : tl("off")} · ${tl("Off-site")} ${health.backup.offsite_state === "configured" ? tl("configured") : tl("Not Configured")}`
              : (health.backup.detail || t("No backup has been captured yet."))}
          </p>
        </Card>
      </div>
    </div>)
  );
};

const BackupTab: React.FC<{ isAdmin: boolean }> = ({ isAdmin }) => {
  const {
    t
  } = useI18n();

  const [cfg, setCfg] = useState<any>(null);
  const [backups, setBackups] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState<any>(null);

  const load = useCallback(() => {
    api.get("/backup/config").then((r) => setCfg(r.data)).catch(() => setCfg(null));
    api.get("/backup/list").then((r) => setBackups(r.data.backups || [])).catch(() => setBackups([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setBusy(true);
    try { await api.post("/backup/run"); toast.success("Encrypted backup created"); load(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Backup failed"); }
    finally { setBusy(false); }
  };
  const verify = async (id: string) => {
    try { const r = await api.post(`/backup/${id}/verify`); toast[r.data.ok ? "success" : "error"](r.data.ok ? tl("Integrity verified") : tl("Integrity check FAILED")); load(); }
    catch { toast.error("Verify failed"); }
  };
  const doPreview = async (id: string) => {
    try { const r = await api.post(`/backup/${id}/restore/preview`); setPreview(r.data); }
    catch { toast.error("Preview failed"); }
  };
  const restoreSandbox = async (id: string) => {
    try { const r = await api.post(`/backup/${id}/restore`, { mode: "verify_sandbox", confirm: true });
      toast[r.data.verified ? "success" : "error"](r.data.verified ? tl("Restore verified in isolated sandbox") : tl("Sandbox restore verification failed")); }
    catch (e: any) { toast.error(e?.response?.data?.detail || "Restore failed"); }
  };

  if (!cfg) return <LoadingBlock />;
  return (
    (<div className="space-y-4" data-testid="backup-tab">
      <Card className="p-4" data-testid="backup-config">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <DatabaseBackup size={18} className="text-brand" />
            <div>
              <p className="text-sm font-medium">{t("Backup & disaster recovery")}</p>
              <p className="text-xs text-muted-foreground">{t(
                "Versioned, integrity-verified, encrypted-at-rest archives with gated restore."
              )}</p>
            </div>
          </div>
          {isAdmin && <Button size="sm" disabled={busy} onClick={run} data-testid="backup-run-btn"><Play size={13} className="mr-1" />{t("Create backup now")}</Button>}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4 text-xs">
          <div className="rounded-md border border-border p-2"><span className="text-muted-foreground">{t("Encryption at rest")}</span><div className="mt-0.5"><StatusPill status={cfg.encryption.enabled ? "good" : "inactive"}>{cfg.encryption.enabled ? t("Enabled") : t("Off")}</StatusPill></div></div>
          <div className="rounded-md border border-border p-2"><span className="text-muted-foreground">{t("Durable local store")}</span><div className="mt-0.5"><StatusPill status="good">{t("Enabled")}</StatusPill></div></div>
          <div className="rounded-md border border-border p-2" data-testid="backup-offsite"><span className="text-muted-foreground">{t("Off-site (S3)")}</span><div className="mt-0.5"><StatusPill status={cfg.offsite.state === "configured" ? "good" : "inactive"}>{cfg.offsite.state === "configured" ? t("Configured") : t("Not Configured")}</StatusPill></div></div>
          <div className="rounded-md border border-border p-2"><span className="text-muted-foreground">{t("Retention")}</span><div className="mt-0.5 font-medium">{cfg.retention_days}{" "}{t("days")}</div></div>
        </div>
        {cfg.offsite.state !== "configured" && <p className="mt-2 text-[11px] text-status-warn">{cfg.offsite.detail}</p>}
      </Card>
      <Card className="p-4" data-testid="backup-list">
        <p className="mb-2 text-sm font-medium">{t("Backup history")}</p>
        {backups.length === 0 ? <p className="text-xs text-muted-foreground">{t("No backups yet.")}</p> : (
          <div className="space-y-2">
            {backups.slice(0, 10).map((b) => (
              <div key={b.backup_id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border p-2" data-testid={`backup-row-${b.backup_id}`}>
                <div className="min-w-0 text-xs">
                  <div className="flex items-center gap-2">
                    <StatusPill status={b.status === "completed" ? "good" : b.status === "failed" ? "critical" : "inactive"}>{stat(b.status)}</StatusPill>
                    <span className="text-muted-foreground">{b.record_count}{" "}{t("records ·")}{" "}{b.collection_count}{" "}{t("collections")}{b.redaction_count ? ` · ${b.redaction_count} redacted` : ""}</span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground truncate">{b.created_at} · {b.encryption?.enabled ? t("encrypted") : t("plaintext")}{b.last_verification ? (b.last_verification.ok ? " · ✓ verified" : " · ✗ verify failed") : ""}</div>
                </div>
                {isAdmin && b.status === "completed" && (
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => verify(b.backup_id)} data-testid={`backup-verify-${b.backup_id}`}><ShieldCheck size={12} className="mr-1" />{t("Verify")}</Button>
                    <Button size="sm" variant="outline" onClick={() => doPreview(b.backup_id)} data-testid={`backup-preview-${b.backup_id}`}>{t("Dry-run")}</Button>
                    <Button size="sm" variant="outline" onClick={() => restoreSandbox(b.backup_id)} data-testid={`backup-restore-${b.backup_id}`}><RotateCcw size={12} className="mr-1" />{t("Restore (sandbox)")}</Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
      {preview && (
        <Card className="p-4" data-testid="backup-preview">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">{t("Restore dry-run preview")}</p>
            <button className="text-xs text-muted-foreground underline" onClick={() => setPreview(null)}>{t("Close")}</button>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{t("Version")}{" "}{preview.manifest_version}({preview.version_compatible ? t("compatible") : "INCOMPATIBLE"}) · {preview.total_records}{" "}{t("records ·")}{" "}{preview.total_conflicts}{" "}{t("existing conflicts · scope")}{" "}{preview.scope}
          </p>
          <p className="mt-1 text-[11px] text-status-warn">{preview.note}</p>
        </Card>
      )}
    </div>)
  );
};

export default Platform;
